/**
 * User-facing copy for send-event-invite Edge Function failures.
 * Shared by useCreateEvent and useUpdateEventFull.
 */

/** Prefix when the event row saved but email dispatch did not. */
export const EVENT_SAVED_INVITE_FAILED =
  "Event saved, but invitations could not be sent";

function rawMessage(err: unknown): string {
  return (
    (err as { message?: string } | null | undefined)?.message?.trim() ??
    String(err)
  );
}

/**
 * Turn an Edge Function invocation error into a host-facing warning.
 * Never throws — email is a side effect after the DB write succeeded.
 */
export function describeEventInviteError(
  err: unknown,
  context?: string,
): string {
  const message = rawMessage(err);
  const ctx = context ? ` (${context})` : "";

  if (/Failed to send a request to the Edge Function/i.test(message)) {
    return (
      `${EVENT_SAVED_INVITE_FAILED}: the browser could not reach the email service. ` +
      "If you are on localhost or a preview URL, redeploy Edge Functions after the latest CORS fix " +
      "or add your origin to the ALLOWED_ORIGINS secret. On production, check your network or ad blocker."
    );
  }

  if (/404|not\s*found|function not/i.test(message)) {
    return `${EVENT_SAVED_INVITE_FAILED}: the send-event-invite Edge Function is not deployed${ctx}.`;
  }

  if (/401|403|unauthor/i.test(message)) {
    return `${EVENT_SAVED_INVITE_FAILED}: the email service rejected our credentials${ctx}.`;
  }

  if (/non-?2xx/i.test(message)) {
    return (
      `${EVENT_SAVED_INVITE_FAILED}: the email service returned an error${ctx}. ` +
      "Check the send-event-invite Edge Function logs in Supabase."
    );
  }

  if (/abort|timeout|timed out/i.test(message)) {
    return (
      `${EVENT_SAVED_INVITE_FAILED}: the request timed out${ctx}. ` +
      "Try again — your event was saved."
    );
  }

  return `${EVENT_SAVED_INVITE_FAILED}: ${message}`;
}

/** Map a 200 response body error field from send-event-invite. */
export function describeEventInvitePayloadError(error: string): string {
  return `${EVENT_SAVED_INVITE_FAILED}: ${error}`;
}
